/**
 * Scraped Perseus-flavoured text (`**bold**`, `$...$`, `![](web+graphie://...)`)
 * → HTML.
 *
 * Math and images are extracted into opaque slots before escaping and the
 * markdown pass, then re-inserted. Otherwise markdown mangles math (`*` → <em>)
 * and escaping mangles LaTeX backslashes.
 *
 * Do not swap in `marked`: it strips the escapes in `$\left\{ x \right\}$` and
 * turns `$a \\ b$` into `$a \ b$`. Both are pinned by tests.
 *
 * Pure and DOM-free; the caller sanitizes (see ProblemText.tsx).
 */

import { imageCandidates } from "./media";

/**
 * Pass-through, deliberately.
 *
 * This used to strip Khan colour macros, which threw the colour away, and also
 * `\small` / `\llap` / `\rlap`, which MathJax does define. It also pre-empted
 * MATHJAX_CONFIG.macros in QuestionBank.tsx, which now defines the Khan macros
 * properly. The corpus is normalized to `\textcolor` upstream, so there is
 * nothing left to strip. Kept because six call sites and types.ts re-export it.
 */
export function cleanLatex(text: string): string {
  return text;
}

/** $$…$$ and \[…\] before $…$ so display math is not split by the inline rule. */
const MATH_RE =
  /\$\$[\s\S]*?\$\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\)|\$(?:\\[\s\S]|[^$\\])+\$/g;

/** ![alt](url) with an optional "title". */
const MD_IMAGE_RE = /!\[([^\]]*)\]\(\s*([^)\s]+)(?:\s+"[^"]*")?\s*\)/g;

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function escapeAttr(s: string): string {
  return escapeHtml(s).replace(/"/g, "&quot;");
}

/**
 * Base DOMPurify policy for question-bank content, extended by ProblemImages
 * for tables. Lives here, not in a component file, so fast refresh keeps
 * working and there is one URI allowlist.
 */
export const QB_SANITIZE = {
  ALLOWED_TAGS: ["img", "strong", "em", "b", "i", "br", "sup", "sub", "span"],
  ALLOWED_ATTR: ["src", "alt", "class", "title", "loading", "decoding"],
  // Drops anything still carrying web+graphie: rather than rendering it broken.
  ALLOWED_URI_REGEXP: /^(?:https?:|data:image\/)/i,
};

/** `**bold**` / `*italic*`. Shared with SurveyRenderer's markdown hook. */
export function applyInlineMarkdown(text: string): string {
  return text
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(?<![<\w])\*([^*]+?)\*(?![>\w])/g, "<em>$1</em>");
}

function unresolvedChipHtml(raw: string, reason: string): string {
  return (
    `<span class="qb-chip qb-unresolved" title="${escapeAttr(raw)}">` +
    `${escapeHtml(reason)}: ${escapeHtml(raw)}</span>`
  );
}

/** Scraped presentational attributes. Sizing is decided by CSS, not the source. */
const PRESENTATION_ATTRS = new Set(["style", "width", "height"]);

/**
 * Matches a whole `<img>` tag without stopping at a `>` inside an attribute
 * value. `[^>]*` is wrong here: `alt="a > b"` is ordinary figure alt text, and
 * truncating there left the real attributes unprocessed.
 */
export const IMG_TAG_RE = /<img\b(?:"[^"]*"|'[^']*'|[^>])*?\/?>/gi;

/** The same, in a JSON.stringify'd config where attribute quotes are `\"`. */
export const JSON_IMG_TAG_RE = /<img\b(?:\\"(?:[^"\\]|\\.)*\\"|[^>])*?\/?>/g;

/** name, then optionally ="…" | ='…' | bare. */
const ATTR_RE = /([^\s=/>]+)(?:\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]*)))?/g;

/**
 * Guarantees an `<img>` carries `qb-inline-img` and no scraped sizing, so every
 * figure obeys question-bank.css wherever it entered from.
 *
 * Tokenizes rather than running find-and-replace over the attributes. The
 * regex version deleted `width=`/`height=`/`style=` wherever they appeared —
 * including inside another attribute's value, where removing the text also
 * removed that value's closing quote. `alt="Rectangle with width=4"` is
 * ordinary scraped alt text, and it silently destroyed the tag: the browser
 * then swallowed the image *and the question text after it*. Worse, the alt
 * that `resolveImageSrcsInJson` repairs from the `images` column is itself
 * untrusted, so the same defect could orphan `src` while leaving the literal
 * `src="…"` text behind — which is exactly what survey-config's placement
 * probe greps for, so the fallback render was suppressed too. No JSON error
 * fired for any of it.
 *
 * Why this is a render-time concern at all: nothing upstream records a figure's
 * intrinsic size. question-scraper/src/khan/perseus.ts:194-199 iterates Perseus
 * `question.images` — a map KEYED by URL whose values are `{width, height}` —
 * and tests `'url' in img` against the value, which is never true. Both the URL
 * and the only true dimensions we were ever handed are discarded; that is why
 * scripts/backfill_graphie_images.py had to exist (coverage was 96 of 6,333
 * refs). True scale normalization would need a viewBox metadata sidecar, not an
 * SVG rewrite — separate ticket, and it belongs in the scraper repo.
 */
export function normalizeImgTag(tag: string): string {
  const body = /^<img\b([\s\S]*?)\/?>$/i.exec(tag);
  if (!body) return tag;

  const kept: string[] = [];
  let classes: string[] = [];

  ATTR_RE.lastIndex = 0;
  for (let a; (a = ATTR_RE.exec(body[1] ?? "")); ) {
    const name = (a[1] ?? "").toLowerCase();
    if (!name) continue;
    if (PRESENTATION_ATTRS.has(name)) continue;
    const value = a[2] ?? a[3] ?? a[4];
    if (name === "class") {
      classes.push(...(value ?? "").split(/\s+/).filter(Boolean));
      continue;
    }
    // Valueless attributes keep their bare form; everything else is re-quoted.
    kept.push(value === undefined ? name : `${name}="${value.replace(/"/g, "&quot;")}"`);
  }

  // Deduplicated so a second pass cannot accumulate the class.
  classes = classes.filter((c) => c !== "qb-inline-img");
  const attrs = kept.length ? ` ${kept.join(" ")}` : "";
  return `<img class="${["qb-inline-img", ...classes].join(" ")}"${attrs} />`;
}

/**
 * The same, for a tag living inside a JSON.stringify'd config.
 *
 * Unescapes into plain HTML, normalizes there, and re-escapes — so there is one
 * implementation of the attribute rules rather than a second set of regexes
 * that have to reason about `\"`. Returns the input untouched if the fragment
 * is not valid JSON string content, since a bad rewrite here yields a config
 * preprocessConfig cannot parse.
 */
export function normalizeImgTagJson(tag: string): string {
  let html: string;
  try {
    html = JSON.parse(`"${tag}"`) as string;
  } catch {
    return tag;
  }
  return JSON.stringify(normalizeImgTag(html)).slice(1, -1);
}

function imageTagHtml(url: string, alt: string): string {
  const candidates = imageCandidates(url);
  const first = candidates[0];
  // Not mirrored to our bucket — show the ref so QA can see what is missing,
  // rather than dropping it and leaving an unexplained gap in the question.
  if (!first) return unresolvedChipHtml(url, "figure not mirrored");

  const fallback = candidates.slice(1).join(" ");
  return (
    `<img src="${escapeAttr(first)}" alt="${escapeAttr(alt)}" loading="lazy" ` +
    `decoding="async" class="qb-inline-img"` +
    (fallback ? ` data-qb-fallback="${escapeAttr(fallback)}"` : "") +
    ` />`
  );
}

/**
 * Returns HTML. NOT sanitized — the only permitted consumer is ProblemText.tsx,
 * which pipes the result through DOMPurify before injecting it.
 */
export function problemTextToHtml(raw: string | null | undefined): string {
  if (!raw) return "";
  let text = cleanLatex(String(raw));

  const slots: { token: string; html: string }[] = [];
  const slot = (html: string): string => {
    const token = `QBSLOT${slots.length}QB`;
    slots.push({ token, html });
    return token;
  };

  // Math first, so a `*` or `_` inside an expression is untouchable.
  text = text.replace(MATH_RE, (m) => slot(escapeHtml(m)));
  text = text.replace(MD_IMAGE_RE, (_m, alt: string, url: string) =>
    slot(imageTagHtml(url, alt)),
  );

  text = applyInlineMarkdown(escapeHtml(text));

  // Reverse order so QBSLOT1QB is restored before QBSLOT10QB's prefix can
  // match, and a function replacement so a `$&` in a URL stays literal.
  for (let i = slots.length - 1; i >= 0; i--) {
    const s = slots[i];
    if (s) text = text.replace(s.token, () => s.html);
  }

  return text;
}

/**
 * Unwraps LaTeX macros to their argument text: `\dfrac{1}{2}` → `12`,
 * `\textcolor{blue}{7}` → `blue7`. Crude, and deliberately so — this feeds a
 * truncated one-line row, never MathJax. Innermost-first so nesting unwinds.
 *
 * Preview-only. cleanLatex stays a pass-through because the rendered paths need
 * the macros intact for MathJax; it is the plain-text rows that cannot show
 * them. 8,022 of 16,943 question_text values carry at least one macro, so
 * without this roughly half the list rows read as raw LaTeX.
 */
function latexToPlain(s: string): string {
  let prev: string;
  do {
    prev = s;
    // Fractions first and with a slash: a bare unwrap turns \dfrac{1}{2} into
    // "12", which a reviewer scanning rows reads as twelve.
    s = s.replace(/\\[dt]?frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g, "$1/$2");
    s = s.replace(/\\[a-zA-Z]+\s*\{([^{}]*)\}/g, "$1");
  } while (s !== prev);
  return s.replace(/\\[a-zA-Z]+/g, "").replace(/[{}]/g, "");
}

/** Plain-text one-liner for truncated list rows — no markup, no math noise. */
export function problemTextToPreview(raw: string | null | undefined): string {
  if (!raw) return "";
  // Images out first, so a URL can never be mistaken for macro syntax.
  return latexToPlain(cleanLatex(String(raw)).replace(MD_IMAGE_RE, "[image] "))
    .replace(/\$\$?/g, "")
    .replace(/\*\*?/g, "")
    .replace(/\s+/g, " ")
    .trim();
}
